import { LogoMark } from "@flexfreight/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useConsoleMe } from "../auth/context";
import type { DashboardData } from "./Dashboard";
import { carrierName } from "./freight/Shipments";

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const clock = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
const ago = (iso: string | null, now: number) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  return m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

/**
 * Wallboard (A.43): the dashboard for an office TV. Today's numbers, what needs attention and carrier on-time,
 * large type on a dark screen, refreshed every minute. Margin and cost are never shown here, whatever the role,
 * because a TV is visible to everyone in the room: the API is asked with view=wall, which leaves them out of the data.
 * There is deliberately no link back into the console from here; staff open the console in their own browser.
 */
export function WallboardPage() {
  const me = useConsoleMe();
  return <Wallboard can={(c) => me.capabilities.includes(c as never)} load={(range) => api<DashboardData>(`/v1/console/dashboard?range=${range}&view=wall`)} />;
}

const KEY_STORE = "ffa-wall-key";
/** The wallboard link's key: taken from the URL fragment once (then removed from the address bar), kept on this TV. */
export function wallKey(): string | null {
  // /wall?staff forgets a stored wallboard key on this browser and goes back to the normal staff sign-in.
  if (new URLSearchParams(window.location.search).has("staff")) {
    try {
      localStorage.removeItem(KEY_STORE);
    } catch {
      /* ignore */
    }
    return null;
  }
  const m = /(?:^#|&)k=(ffw_[\w-]{20,})/.exec(window.location.hash);
  if (m) {
    try {
      localStorage.setItem(KEY_STORE, m[1]!);
    } catch {
      /* storage blocked: the key still works until the page is reloaded */
    }
    window.history.replaceState(null, "", window.location.pathname);
    return m[1]!;
  }
  try {
    return localStorage.getItem(KEY_STORE);
  } catch {
    return null;
  }
}

/**
 * Kiosk wallboard (opened with a wallboard link): no staff session at all. The key reads only /v1/wall/dashboard,
 * which returns the read-only role's numbers without cost, margin or customer revenue. A revoked key shows a notice.
 */
export function KioskWallboard({ wallKeyValue }: { wallKeyValue: string }) {
  const READ_ONLY = new Set(["shipments:read", "customers:read", "tickets:read"]);
  const load = async (range: "today" | "7d") => {
    const r = await fetch(`/v1/wall/dashboard?range=${range}`, { headers: { "x-wall-key": wallKeyValue }, credentials: "omit", cache: "no-store" });
    if (r.status === 401) {
      try {
        localStorage.removeItem(KEY_STORE);
      } catch {
        /* ignore */
      }
      throw new Error("revoked");
    }
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return (await r.json()) as DashboardData;
  };
  return <Wallboard can={(c) => READ_ONLY.has(c)} load={load} kiosk />;
}

function Wallboard({ can, load, kiosk = false }: { can: (cap: string) => boolean; load: (range: "today" | "7d") => Promise<DashboardData>; kiosk?: boolean }) {
  const q = useQuery({ queryKey: ["console", "dashboard", "today", "wall", kiosk], queryFn: () => load("today"), refetchInterval: 60_000, placeholderData: keepPreviousData, retry: (n, e) => !(e instanceof Error && e.message === "revoked") && n < 2 });
  // Carrier on-time needs more than one day of deliveries to mean anything.
  const week = useQuery({ queryKey: ["console", "dashboard", "7d", "wall", kiosk], queryFn: () => load("7d"), refetchInterval: 300_000, placeholderData: keepPreviousData, enabled: can("shipments:read"), retry: false });
  const revoked = q.error instanceof Error && q.error.message === "revoked";
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    document.title = "Wallboard · FlexFreight Admin";
    return () => clearInterval(t);
  }, []);
  const d = q.data;
  const tiles: { label: string; value: string; tone?: "danger" | "warning" }[] = d
    ? [
        { label: "Quotes today", value: String(d.kpis.quotes.value) },
        { label: "Booked today", value: String(d.kpis.bookings.value) },
        { label: "Revenue today", value: usd.format(d.kpis.revenue.value.cents / 100) },
        ...(can("shipments:read") ? [{ label: "In motion", value: String(d.kpis.activeShipments) }, { label: "Exceptions", value: String(d.kpis.exceptions), ...(d.kpis.exceptions ? { tone: "danger" as const } : {}) }] : []),
        ...(d.kpis.openTickets !== null ? [{ label: "Open tickets", value: String(d.kpis.openTickets) }] : []),
        ...(can("customers:read") ? [{ label: "Signups to review", value: String(d.kpis.signupsPending), ...(d.kpis.signupsPending ? { tone: "warning" as const } : {}) }] : []),
      ]
    : [];
  return (
    <div className="ffa-wall ff-dark-scope">
      <header className="ffa-wall__head">
        <span className="ffa-wall__brand"><LogoMark className="ffa-wall__mark" title="" /> FlexFreight</span>
        <span className="ffa-wall__clock">{clock.format(new Date(now))} ET</span>
      </header>
      {revoked ? <p className="ffa-wall__note" role="alert">This wallboard link was turned off. Ask a FlexFreight admin for a new link.</p> : q.isError && !d ? <p className="ffa-wall__note" role="alert">Can&apos;t reach the console right now. Retrying every minute.</p> : null}
      {revoked ? null : (
      <>
      <section className="ffa-wall__tiles" aria-label="Today">
        {tiles.map((t) => (
          <div key={t.label} className="ffa-wall__tile" data-tone={t.tone}>
            <span className="ffa-wall__label">{t.label}</span>
            <span className="ffa-wall__value">{t.value}</span>
          </div>
        ))}
      </section>
      <div className="ffa-wall__grid">
        {can("shipments:read") ? (
          <section className="ffa-wall__panel" aria-labelledby="wall-issues">
            <h2 id="wall-issues">Needs attention</h2>
            {!d || d.issues.length === 0 ? (
              <p className="ffa-wall__note">No shipment exceptions.</p>
            ) : (
              <ul>
                {d.issues.slice(0, 6).map((i) => (
                  <li key={i.id}>
                    <strong>{i.bolNumber ?? "Shipment"}</strong> · {carrierName(i.carrier)} · {i.orgName}
                    <span>{i.description ?? "Exception"}{i.at ? ` · ${ago(i.at, now)}` : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
        {can("shipments:read") ? (
          <section className="ffa-wall__panel" aria-labelledby="wall-carriers">
            <h2 id="wall-carriers">Carrier on-time (last 7 days)</h2>
            {!week.data || week.data.carriers.length === 0 ? (
              <p className="ffa-wall__note">No deliveries in the last 7 days.</p>
            ) : (
              <ul className="ffa-wall__bars">
                {week.data.carriers.slice(0, 8).map((c) => (
                  <li key={c.carrier}>
                    <span>{carrierName(c.carrier)}</span>
                    <span className="ffa-wall__bar" aria-hidden="true"><span style={{ inlineSize: `${c.onTimePct ?? 0}%` }} /></span>
                    <span className="ffa-wall__pct">{c.onTimePct === null ? "-" : `${c.onTimePct}%`}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
      </div>
      </>
      )}
      <p className="ffa-wall__foot">{d && q.dataUpdatedAt ? `Updated ${new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }).format(new Date(q.dataUpdatedAt))} ET` : "Loading"} · refreshes every minute</p>
    </div>
  );
}
